/**
 * Client Success → Users (ADR-0077, Phase 1).
 *
 * The portal's `Admin · Users` roster, ported read-only into CC: a flat table
 * of Name · Email · Firm · Role · Status, with a name/email/firm search. No
 * detail panel — the data is flat and meant for fast scanning. The server
 * returns it pre-sorted (Inactive-first, then firm, then name), so a
 * deactivated user is never lost at the bottom.
 *
 * Read-only. The portal's write actions (+ Add user, per-row Message / Edit /
 * Deactivate-Reactivate) render but are inert.
 *
 * Spec: engineering/command-center/docs/client-success/phase-1-spec.md (evicted 2026-08-29, #775 round 10)
 */

import { useState } from "react";
import type { ClientSuccessUser } from "@command-center/shared";
import { ClientSuccessNav } from "@/components/client-success-nav";
import { SearchBar } from "@/components/search-bar";
import { UserDetailPanel } from "@/components/user-detail-panel";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import {
  useClientSuccessUserDetail,
  useClientSuccessUsers,
  useDeleteGoodStanding,
  useUploadGoodStanding,
} from "@/lib/use-client-success-users";
import { ClientError } from "@/lib/api";
import { useMe } from "@/lib/use-me";
import { usePanelSelection } from "@/lib/use-panel-selection";
import { CreateTaskModal } from "@/routes/dashboard/create-task-modal";

export function UsersPage() {
  const users = useClientSuccessUsers();
  const me = useMe();
  // Detail-panel selection is URL-driven (`?user=<id>`) so deep-links from
  // the Dashboard row Identity Link open the panel on mount, and refresh
  // survives. See `usePanelSelection` (Slice I).
  const panel = usePanelSelection("user");
  const detailQuery = useClientSuccessUserDetail(panel.selectedId);
  const [query, setQuery] = useState("");
  // `+ Ask` (Slice I) opens the CreateTaskModal pre-bound to the open panel's
  // user. The flag mounts the modal — `initialAbout` rides the open user id.
  const [askOpen, setAskOpen] = useState(false);
  // Good-standing certificate (issue #416) — the one wired write on the user
  // panel. Pending/error are shared across upload + delete (only one runs at a
  // time on the open panel).
  const gsUpload = useUploadGoodStanding();
  const gsDelete = useDeleteGoodStanding();
  const [goodStandingError, setGoodStandingError] = useState<string | null>(
    null,
  );

  if (users.isLoading) {
    return (
      <Shell>
        <div className="p-8 text-sm text-plane-muted">Loading…</div>
      </Shell>
    );
  }

  if (users.isError) {
    return (
      <Shell>
        <div className="p-8 text-sm text-error" data-testid="users-error">
          Couldn't load users. It will retry automatically.
        </div>
      </Shell>
    );
  }

  const all = users.data?.users ?? [];
  const searching = query.trim() !== "";
  // Search across name, email, and firm — the three things you'd scan for.
  const visible = all.filter((u) => matchesUser(u, query));
  // A deep-link with a stale `?user=<id>` produces a 404 on the per-id route
  // → detailQuery.isError. Surface a banner with a Clear affordance so the
  // operator sees feedback instead of the panel silently failing to mount.
  const staleUserLink = panel.selectedId !== null && detailQuery.isError;

  // The open panel's user id (null when no panel) — captured here so the
  // good-standing handlers stay TS-narrowed without a non-null assertion.
  const openUserId = detailQuery.data?.user.id ?? null;
  function uploadGoodStanding(file: File) {
    if (!openUserId) return;
    setGoodStandingError(null);
    gsUpload.mutate(
      { userId: openUserId, file },
      { onError: (err) => setGoodStandingError(formatGoodStandingError(err)) },
    );
  }
  function deleteGoodStanding() {
    if (!openUserId) return;
    setGoodStandingError(null);
    gsDelete.mutate(
      { userId: openUserId },
      { onError: (err) => setGoodStandingError(formatGoodStandingError(err)) },
    );
  }

  return (
    <Shell>
      <div className="mb-3 flex items-center justify-between">
        <h1 className="text-lg font-sans font-semibold">
          Users
          <span className="ml-2 text-sm font-normal text-plane-muted" data-testid="users-count">
            {all.length}
          </span>
        </h1>
        <InertButton label="+ Add user" testId="users-add" />
      </div>

      {staleUserLink ? (
        <div
          className="mb-3 flex items-center justify-between rounded-md border border-warning-border bg-warning-bg px-3 py-2 text-sm text-warning"
          data-testid="users-stale-link"
          role="status"
        >
          <span>This link doesn't point at a user we know about anymore.</span>
          <button
            type="button"
            onClick={() => panel.closePanel()}
            className="rounded-md border border-warning-border px-2 py-1 text-xs font-medium hover:bg-warning-bg"
            data-testid="users-stale-link-clear"
          >
            Clear
          </button>
        </div>
      ) : null}

      <SearchBar
        value={query}
        onChange={setQuery}
        placeholder="Search by name, email, or firm…"
        testId="users-search"
      />

      {visible.length === 0 ? (
        <div
          className="flex min-h-[40vh] items-center justify-center text-center"
          data-testid="users-empty"
        >
          <p className="text-lg text-plane-muted">
            {searching ? "No matches." : "No users."}
          </p>
        </div>
      ) : (
        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="cursor-default hover:bg-transparent">
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Firm</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.map((user) => (
                <UserRow
                  key={user.id}
                  user={user}
                  onOpen={() => panel.openPanel(user.id)}
                />
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {panel.selectedId && detailQuery.data ? (
        <>
          <UserDetailPanel
            user={detailQuery.data.user}
            onClose={() => {
              panel.closePanel();
              setAskOpen(false);
              setGoodStandingError(null);
            }}
            onAsk={me.data ? () => setAskOpen(true) : undefined}
            onUploadGoodStanding={uploadGoodStanding}
            onDeleteGoodStanding={deleteGoodStanding}
            goodStandingPending={gsUpload.isPending || gsDelete.isPending}
            goodStandingError={goodStandingError}
          />
          {askOpen && me.data ? (
            <CreateTaskModal
              // Key on the bound entity so a URL-driven panel switch while
              // the modal is open remounts the modal with fresh state —
              // otherwise submit would create against the previous entity.
              key={`user:${detailQuery.data.user.id}`}
              open
              onClose={() => setAskOpen(false)}
              viewerEmail={me.data.email}
              initialAbout={{ kind: "user", id: detailQuery.data.user.id }}
            />
          ) : null}
        </>
      ) : null}
    </Shell>
  );
}

/** Page chrome shared across load / error / ready so the sub-nav is always up. */
function Shell({ children }: { children: React.ReactNode }) {
  return (
    <section className="mx-auto max-w-5xl p-6" data-testid="firm-success-users">
      <ClientSuccessNav active="users" />
      {children}
    </section>
  );
}

/** Case-insensitive match across name, email, and firm name. Empty → all. */
function matchesUser(u: ClientSuccessUser, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (q === "") return true;
  return (
    u.name.toLowerCase().includes(q) ||
    (u.email ?? "").toLowerCase().includes(q) ||
    (u.firmName ?? "").toLowerCase().includes(q)
  );
}

/** Map a good-standing upload/delete error to a one-line panel message. */
function formatGoodStandingError(err: Error): string {
  if (err instanceof ClientError) {
    const body = err.body as { error?: string } | null;
    switch (body?.error) {
      case "file_too_large":
        return "File is too large (max 100 MB).";
      case "unsupported_mime_type":
        return "Unsupported file type — use a PDF or an image (PNG/JPEG/WebP).";
      case "invalid_document_body":
        return "That file couldn't be read. Try another.";
      default:
        return "Couldn't save the document — it will not have changed.";
    }
  }
  return "Couldn't reach the server. Try again.";
}

function UserRow({
  user,
  onOpen,
}: {
  user: ClientSuccessUser;
  onOpen: () => void;
}) {
  const inactive = user.status === "Inactive";
  return (
    <TableRow
      // Slice I made the row clickable. <tr> isn't natively focusable, so the
      // role / tabIndex / onKeyDown trio is what makes Enter or Space open the
      // detail panel — keyboard parity with the row's onClick.
      className={cn(inactive && "opacity-60", "cursor-pointer")}
      data-testid="users-row"
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      <TableCell className="font-medium">{user.name}</TableCell>
      <TableCell
        className={user.email ? "break-all" : "text-muted-foreground"}
        data-testid="users-email"
      >
        {user.email ?? "—"}
      </TableCell>
      <TableCell
        className={user.firmName ? undefined : "text-muted-foreground"}
        data-testid="users-firm"
      >
        {user.firmName ?? "—"}
      </TableCell>
      <TableCell data-testid="users-role">
        {user.role ?? <span className="text-muted-foreground">—</span>}
      </TableCell>
      <TableCell data-testid="users-status">
        <Badge variant={inactive ? "muted" : "emerald"}>
          {user.status ?? "—"}
        </Badge>
      </TableCell>
      <TableCell className="w-0 text-right" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-end gap-1.5">
          <InertButton label="Edit" testId="users-row-edit" small />
          <InertButton
            label={inactive ? "Reactivate" : "Deactivate"}
            testId="users-row-deactivate"
            small
          />
        </div>
      </TableCell>
    </TableRow>
  );
}

/** A rendered-but-inert action button (Phase 1). */
function InertButton({
  label,
  testId,
  small = false,
}: {
  label: string;
  testId: string;
  small?: boolean;
}) {
  return (
    <button
      type="button"
      disabled
      title="Not wired yet — actions land in a later phase"
      data-testid={testId}
      className={cn(
        "cursor-not-allowed rounded-md border font-medium text-muted-foreground opacity-60",
        small ? "px-2 py-1 text-xs" : "px-3 py-1.5 text-sm",
      )}
    >
      {label}
    </button>
  );
}
